<template>
  <div>
    <b-row
      class="margin-0 font-size-14 color-complementary-300 padding-x-16 padding-top-16 padding-bottom-0"
      :class="{ 'background-light-gray': item.isAbsentId }"
    >
      <b-col cols="" class="padding-0">
        <b-row v-if="item.personName" class="margin-0">
          <b-col cols="12" class="margin-bottom-8 padding-0">
            <AvatarGroup
              :data="generatePersonData"
              :userItem="{
                full_name: item.personName,
                person_code: item.personCode,
                parvandeh_code: item.parvandehCode,
                image_address: item.personAvatar,
              }"
              :hasType="false"
              :secondCaptionTag="secondCaptionTag"
            />
          </b-col>
          <b-col
            v-if="hasSecondPerson"
            cols="12"
            class="margin-bottom-8 padding-0"
          >
            <AvatarGroup
              :data="generateSecondPersonData"
              :userItem="{
                full_name: item.secondPersonName,
                person_code: item.secondPersonCode,
                parvandeh_code: item.secondPersonParvandehCode,
                image_address: item.secondPersonAvatar,
              }"
              :hasType="false"
              :secondCaptionTag="secondCaptionTag"
            />
          </b-col>
        </b-row>
        <b-row class="margin-0">
          <b-col cols="12" class="padding-0 margin-bottom-8">
            {{ $lang.executiveRoleInCenterExecute }}:
            {{ item.executiveRoleCaption }}
            <span class="color-complementary-100">|</span>
            {{ item.personCenterExecuteCode }}
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="auto" class="padding-0 flex-center padding-bottom-16">
        <span
          v-if="item.isAbsentId"
          class="background-complementary-100 color-complementary-400 font-size-12 font-weight-500 padding-x-8 padding-y-4 border-radius-4"
        >
          {{ item.isAbsentCaption }}
        </span>
        <MenuPopupWrapper
          v-if="!item.isAbsentId && !!itemButtons.length"
          iconClass="margin-right-8 color-complementary-200 cursor--pointer"
          menuClass="left-0 top-4 z-index-1 padding-top-24"
          :items="itemButtons"
          :permissions="permissions"
          :showMenu="currentItemMenuId === item.id"
          :rowItem="item"
          @click="handleShowItemMenu"
          @close="handleCloseItemMenu"
        >
          <template #bottom-area>
            <Switchbox
              v-if="hasCreateReportChange"
              :label="$lang.createReportChange"
              :value="item.isCreateReport"
              name="isCreateReport"
              boxClass="padding-x-8 flex-between"
              :loading="createReportChangeLoading"
              @handleChange="handleSetCenterExecutePersonReport(item)"
            />
          </template>
        </MenuPopupWrapper>
      </b-col>
    </b-row>
    <Modal
      :show="isSetAbsentModal"
      :title="$lang.setAbsent"
      :closeOnBackdrop="false"
      :hasFooter="false"
      :hasClose="false"
      :okButton="false"
      size="sm"
      @close="handleCloseSetAbsentModal"
    >
      <template #body>
        <SetAbsentForm
          :personId="currentPersonId"
          @handleClose="handleCloseSetAbsentModal"
        />
      </template>
    </Modal>
    <EditPersonPin
      :visible="isEditViewPin"
      :item="item"
      @handleClose="handleCloseEditPin"
    />
    <Modal
      :show="isRemoveModal"
      :title="$lang.removePerson"
      :closeOnBackdrop="false"
      :hasClose="false"
      :okButton="true"
      :okButtonText="$lang.remove"
      okButtonVariant="filled-error"
      size="sm"
      :ok="handleRemoveCenterExecutePerson"
      @close="handleCloseRemoveModal"
    >
      <template #body>
        <div class="margin-bottom-24 font-size-14 color-complementary-300">
          {{ $lang.removeSureMessage }}
        </div>
      </template>
    </Modal>
    <ParvandehReportsListPin
      :visible="isParvandehReportsListPin"
      :code="parvandehCode"
      :title="parvandehReportsListPinTitle"
      :saltFileName="`${parvandehNumber}_${personCenterExecuteCode}`"
      @close="handleCloseParvandehReportsListPin"
    />
    <Modal
      :show="isParvandehReportsUploaderModal"
      :title="`${$lang.uploadReportFor} ${personName}`"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="handleCloseParvandehReportsUploaderPin"
    >
      <template #body>
        <ParvandehReportsUploader
          resourceName="center_execute"
          :resourceValue="centerExecuteCode"
          :parvandehCode="parvandehCode"
          @handleClose="handleCloseParvandehReportsUploaderPin"
          @handleReloadList="handleReloadList"
        />
      </template>
    </Modal>
    <Modal
      :show="isRebuildCenterExecuteReportModal"
      :title="`${$lang.rebuildReportsFor} ${personName}`"
      :closeOnBackdrop="false"
      :hasClose="true"
      :hasFooter="true"
      size="sm"
      :okButton="true"
      okButtonVariant="filled-primary"
      :okButtonText="$lang.buildReport"
      :ok="handleRebuildCenterExecuteReport"
      @close="handleCloseRebuildCenterExecuteReportModal"
    >
      <template #body>
        <div class="margin-bottom-24 font-size-14 color-complementary-300">
          {{ $lang.rebuildReportConfirm }}
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import {
  MenuPopupWrapper,
  Modal,
  ParvandehReportsListPin,
  ParvandehReportsUploader,
  AvatarGroup,
} from "@/components/re-design";
import { Switchbox } from "@/components/re-design/forms";
import SetAbsentForm from "./SetAbsentForm.vue";
import EditPersonPin from "./EditPersonPin.vue";
import { pathOr, includes } from "ramda";
import { computed, inject, ref, toRefs } from "vue";
import {
  rebuildCenterExecuteReport,
  removeCenterExecutePerson,
  setCenterExecutePersonReport,
} from "@/modules/kanoon/services";
import {
  checkScreenDevice,
  generateCurrentRoute,
  generateError,
  generateLang,
  toastAlert,
} from "@/shared/utils";
export default {
  components: {
    MenuPopupWrapper,
    Modal,
    ParvandehReportsListPin,
    ParvandehReportsUploader,
    Switchbox,
    SetAbsentForm,
    EditPersonPin,
    AvatarGroup,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    hasCreateReportChange: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    // bind props
    const { item } = toRefs(props);

    const generatePersonData = computed(() => {
      return {
        type: item.value.executiveRoleCaption,
        id: item.value.parvandehNumber,
        personCode: item.value.personCode,
        name: item.value.personName,
        secondCaption: `<span class="color-complementary-100 mobile-none"> | </span>${generateLang(
          ["nationalCode"]
        )}: ${item.value.personNationalCode}`,
        images: [item.value.personAvatar],
      };
    });
    const generateSecondPersonData = computed(() => {
      return {
        type: item.value.executiveRoleCaption,
        id: item.value.secondPersonParvandehNumber,
        personCode: item.value.secondPersonCode,
        name: item.value.secondPersonName,
        secondCaption: `<span class="color-complementary-100 mobile-none"> | </span>${generateLang(
          ["nationalCode"]
        )}: ${item.value.secondPersonNationalCode}`,
        images: [item.value.secondPersonAvatar],
      };
    });

    const secondCaptionTag = computed(() =>
      checkScreenDevice(["tablet", "desktop"]) ? "span" : "div"
    );

    // bind injects data
    const centerExecuteData = inject("CENTER_EXECUTE_DATA");
    const { code: centerExecuteCode } = centerExecuteData.value;
    const hasSecondPerson = computed(() =>
      pathOr(null, ["secondPerson"], item.value)
    );
    const executiveRoleId = computed(() =>
      pathOr(null, ["executiveRoleId"], item.value)
    );
    const secondPersonTitle =
      executiveRoleId.value === 2
        ? generateLang(["secondPerson"])
        : generateLang(["kanoonAssistantManager"]);

    const permissions = inject("PERMISSIONS");

    // handle parent emits
    const handleReloadList = inject("handleReloadList");

    // handle set absent
    const isSetAbsentModal = ref(false);
    const currentPersonData = ref(null);
    const currentPersonId = computed(() =>
      pathOr(null, ["id"], currentPersonData.value)
    );
    const handleOpenSetAbsentModal = (item) => {
      currentPersonData.value = item;
      isSetAbsentModal.value = true;
    };
    const handleCloseSetAbsentModal = () => (isSetAbsentModal.value = false);

    // handle edit
    const isEditViewPin = ref(false);
    const handleOpenEditPin = () => (isEditViewPin.value = true);
    const handleCloseEditPin = () => (isEditViewPin.value = false);

    // handle view
    const handleOpenViewPin = () => (isEditViewPin.value = true);

    // handle remove
    const isRemoveModal = ref(false);
    const handleOpenRemoveModal = () => (isRemoveModal.value = true);
    const handleCloseRemoveModal = () => (isRemoveModal.value = false);
    const handleRemoveCenterExecutePerson = async () => {
      try {
        const { id: centerExecuteId } = centerExecuteData.value;
        const personCenterExecuteId = pathOr(null, ["id"], item.value);
        const response = await removeCenterExecutePerson(
          centerExecuteId,
          personCenterExecuteId
        );
        const { message } = response;
        toastAlert(message);
        handleCloseRemoveModal();
        handleReloadList();
      } catch (error) {
        generateError(error);
      }
    };

    // handle show parvandeh reports
    const parvandehCode = computed(() =>
      pathOr(null, ["parvandehCode"], item.value)
    );
    const parvandehNumber = computed(() =>
      pathOr("", ["parvandehNumber"], item.value)
    );
    const personCenterExecuteCode = computed(() =>
      pathOr(null, ["personCenterExecuteCode"], item.value)
    );
    const personName = computed(() => pathOr("", ["personName"], item.value));
    const parvandehReportsListPinTitle = computed(
      () => `${generateLang(["reportsFor"])} ${personName.value}`
    );
    const isParvandehReportsListPin = ref(false);
    const handleOpenParvandehReportsListPin = () =>
      (isParvandehReportsListPin.value = true);
    const handleCloseParvandehReportsListPin = () =>
      (isParvandehReportsListPin.value = false);

    // handle shaw parvandeh reports uploader
    const isParvandehReportsUploaderModal = ref(false);
    const handleOpenParvandehReportsUploaderPin = () =>
      (isParvandehReportsUploaderModal.value = true);
    const handleCloseParvandehReportsUploaderPin = () =>
      (isParvandehReportsUploaderModal.value = false);

    // handle set person report with switchbox
    const createReportChangeLoading = ref(false);
    const handleSetCenterExecutePersonReport = async (item) => {
      try {
        createReportChangeLoading.value = true;
        const centerExecuteCode = pathOr(
          null,
          ["code"],
          centerExecuteData.value
        );
        const personId = item.id;
        const { isCreateReport } = item;
        // reverse and change boolean type to number
        const reverseIsCreateReport = +!isCreateReport;
        const response = await setCenterExecutePersonReport(
          centerExecuteCode,
          personId,
          reverseIsCreateReport
        );
        const { message } = response;
        toastAlert(message);
      } catch (error) {
        generateError(error);
        handleReloadList();
      } finally {
        createReportChangeLoading.value = false;
      }
    };

    // handle menu items
    const currentItemMenuId = ref(null);
    const handleShowItemMenu = (item) => {
      const id = pathOr(null, ["id"], item);
      currentItemMenuId.value = currentItemMenuId.value === id ? null : id;
    };
    const handleCloseItemMenu = () => (currentItemMenuId.value = null);

    // handle rebuild center execute single report
    const currentItem = ref(null);
    const isRebuildCenterExecuteReportModal = ref(false);
    const handleOpenRebuildCenterExecuteReportModal = (item) => {
      currentItem.value = item;
      isRebuildCenterExecuteReportModal.value = true;
    };
    const handleCloseRebuildCenterExecuteReportModal = () => {
      currentItem.value = null;
      isRebuildCenterExecuteReportModal.value = false;
    };
    const handleRebuildCenterExecuteReport = async () => {
      try {
        const centerExecuteCode = pathOr(
          null,
          ["code"],
          centerExecuteData.value
        );
        const centerExecutePersonId = pathOr(null, ["id"], currentItem.value);
        const response = await rebuildCenterExecuteReport(
          centerExecuteCode,
          centerExecutePersonId
        );
        const { message } = response;
        toastAlert(message);
        handleCloseRebuildCenterExecuteReportModal();
      } catch (error) {
        generateError(error);
      }
    };

    // generate item buttons menu
    const itemButtons = computed(() => {
      const isCenterExecutePlannerPage =
        generateCurrentRoute() === "center-execute-planner-edit";
      const buttons = [
        {
          caption: generateLang(["edit"]),
          icon: "pencil-outline",
          show: true,
          permissions: ["admin", "edit", "view"],
          click: handleOpenEditPin,
        },
        {
          caption: generateLang(["setAbsent"]),
          icon: "account-cancel-outline",
          show: true,
          permissions: ["admin", "edit"],
          allowCenterExecuteStates: [1, 2, 3, 4],
          allowExecutiveRoles: [1],
          click: handleOpenSetAbsentModal,
        },
        {
          caption: generateLang(["remove"]),
          icon: "trash-can-outline",
          show: !isCenterExecutePlannerPage,
          permissions: ["admin"],
          allowIsPlanningPersons: [0],
          allowCenterExecuteStates: [1, 2],
          click: handleOpenRemoveModal,
        },
        {
          caption: generateLang(["reports"]),
          icon: "chart-box-outline",
          show: !isCenterExecutePlannerPage,
          permissions: ["admin", "edit"],
          allowCenterExecuteStates: [2, 3, 4, 5],
          allowExecutiveRoles: [1],
          hasCheckCandidate: true,
          click: handleOpenParvandehReportsListPin,
        },
        {
          caption: generateLang(["uploadReport"]),
          icon: "file-upload-outline",
          show: !isCenterExecutePlannerPage,
          permissions: ["admin", "edit", "view"],
          allowCenterExecuteStates: [5],
          allowExecutiveRoles: [1],
          hasCheckCandidate: true,
          click: handleOpenParvandehReportsUploaderPin,
        },
        {
          caption: generateLang(["rebuild"]),
          icon: "recycle-variant",
          show: !isCenterExecutePlannerPage,
          permissions: ["admin", "edit", "view"],
          allowCenterExecuteStates: [3, 4, 5],
          allowExecutiveRoles: [1],
          hasCheckCandidate: true,
          click: handleOpenRebuildCenterExecuteReportModal,
        },
      ];
      return buttons.filter((button) => {
        const allowIsPlanningPersons = pathOr(
          [],
          ["allowIsPlanningPersons"],
          button
        );
        const allowCenterExecuteStates = pathOr(
          [],
          ["allowCenterExecuteStates"],
          button
        );
        const allowExecutiveRoles = pathOr([], ["allowExecutiveRoles"], button);

        const {
          isPlanningPerson,
          centerExecuteState,
          executiveRoleId,
          personCode,
          isAbsentId,
        } = item.value;

        let condition = button.show;

        // check is planning person
        if (allowIsPlanningPersons.length) {
          condition =
            condition && includes(isPlanningPerson, allowIsPlanningPersons);
        }

        // check states
        if (allowCenterExecuteStates.length) {
          condition =
            condition && includes(centerExecuteState, allowCenterExecuteStates);
        }

        // check executive roles
        if (allowExecutiveRoles.length) {
          condition =
            condition && includes(executiveRoleId, allowExecutiveRoles);
        }

        // check is candidate
        const hasCheckCandidate = pathOr(false, ["hasCheckCandidate"], button);
        const isCandidate = executiveRoleId === 1;
        if (hasCheckCandidate && isCandidate) {
          condition = condition && personCode;
        }

        // check is not absent
        const isAbsent = !!isAbsentId;
        condition = condition && !isAbsent;

        return condition;
      });
    });

    // return data to be used in template
    return {
      item,
      permissions,
      itemButtons,
      currentItemMenuId,
      handleShowItemMenu,
      handleCloseItemMenu,
      isSetAbsentModal,
      currentPersonId,
      currentPersonData,
      handleCloseSetAbsentModal,
      handleReloadList,
      includes,
      createReportChangeLoading,
      handleSetCenterExecutePersonReport,
      isEditViewPin,
      handleCloseEditPin,
      isRemoveModal,
      handleCloseRemoveModal,
      handleRemoveCenterExecutePerson,
      parvandehCode,
      parvandehNumber,
      personCenterExecuteCode,
      parvandehReportsListPinTitle,
      isParvandehReportsListPin,
      handleCloseParvandehReportsListPin,
      centerExecuteCode,
      personName,
      isParvandehReportsUploaderModal,
      handleCloseParvandehReportsUploaderPin,
      hasSecondPerson,
      secondPersonTitle,
      generatePersonData,
      generateSecondPersonData,
      secondCaptionTag,
      isRebuildCenterExecuteReportModal,
      handleCloseRebuildCenterExecuteReportModal,
      handleRebuildCenterExecuteReport,
    };
  },
};
</script>
